import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, Spinner } from '../../components/ui';
import { AgreementPage, BASE, keys, statusLabel, statusTone } from './api';
import { AgreementDrawer } from './AgreementDrawer';

/**
 * The "Agreements" section of the Today board's visit drawer. Shows nothing at all to people without
 * `agreements.view`. The Today page imports this lazily when the agreements module is enabled.
 */
export function VisitAgreements({ visitId }: { visitId: string }) {
  const allowed = useCan('agreements.view');
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: keys.visit(visitId),
    queryFn: () => api.get(BASE, { query: { visitId, pageSize: 50 }, schema: AgreementPage }),
    enabled: allowed,
  });
  if (!allowed) return null;
  return (
    <section aria-labelledby={`visit-agreements-${visitId}`} className="space-y-2">
      <h3 id={`visit-agreements-${visitId}`} className="font-semibold">
        {t('agreementsUi.visit.title')}
      </h3>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.items.length === 0 ? <p className="text-sm text-slate-700">{t('agreementsUi.visit.none')}</p> : null}
      {q.data && q.data.items.length > 0 ? (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {q.data.items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <button type="button" className="text-left font-medium text-brand hover:underline" onClick={() => setOpen(a.id)}>
                {t('agreementsUi.visit.item', { title: a.noticeTitle, version: a.noticeVersion, when: formatDateTime(a.acceptedAt) })}
              </button>
              <Badge tone={statusTone(a.status)}>{statusLabel(a.status)}</Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {open ? <AgreementDrawer id={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}
